import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function CreateLinuxQuest() {
  const navigate = useNavigate();
  const { questId } = useParams();
  const isEditMode = !!questId;
  
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  
  const [quest, setQuest] = useState({
    title: '',
    description: '',
    levels: [],
    category_id: null,
    is_public: true,
    assigned_courses: [],
    use_real_terminal: false,
    allow_internet: false,
    persistent_containers: false,
    docker_image: 'alpine:latest'
  });
  
  const [categories, setCategories] = useState([]);
  
  const [currentLevel, setCurrentLevel] = useState({
    level_number: 1,
    title: '',
    description: '',
    hint: '',
    filesystem: {},
    password_next_level: '',
    commands_hint: []
  });
  
  const [editingFile, setEditingFile] = useState(null);
  const [newFilename, setNewFilename] = useState('');
  const [newFileContent, setNewFileContent] = useState('');
  const [editingLevelIndex, setEditingLevelIndex] = useState(null);
  const [classroomCourses, setClassroomCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(false);

  useEffect(() => {
    if (isEditMode) {
      fetchQuest();
    }
    fetchClassroomCourses();
    fetchCategories();
  }, [questId]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/categories/${user.id}`);
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const fetchClassroomCourses = async () => {
    try {
      setLoadingCourses(true);
      const response = await axios.get(`${BACKEND_URL}/api/classroom/courses/${user.id}`);
      // El endpoint devuelve directamente los cursos
      if (response.data && Array.isArray(response.data)) {
        setClassroomCourses(response.data);
      } else if (response.data.courses) {
        setClassroomCourses(response.data.courses);
      }
    } catch (error) {
      console.error('Error fetching classroom courses:', error);
      // Si falla, simplemente dejamos la lista vacía
      setClassroomCourses([]);
    } finally {
      setLoadingCourses(false);
    }
  };

  const fetchQuest = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/linux-quest/${questId}`);
      setQuest(response.data);
    } catch (error) {
      console.error('Error fetching quest:', error);
      alert('Error al cargar el quest');
    }
  };

  const handleAddFile = () => {
    if (!newFilename.trim()) {
      alert('Por favor ingresa un nombre de archivo');
      return;
    }
    
    setCurrentLevel({
      ...currentLevel,
      filesystem: {
        ...currentLevel.filesystem,
        [newFilename]: newFileContent
      }
    });
    
    setNewFilename('');
    setNewFileContent('');
  };

  const handleRemoveFile = (filename) => {
    const newFs = { ...currentLevel.filesystem };
    delete newFs[filename];
    setCurrentLevel({ ...currentLevel, filesystem: newFs });
  };

	const handleEditFile = (filename) => {
	  setEditingFile(filename);
	  setNewFilename(filename);
	  setNewFileContent(currentLevel.filesystem[filename]);
	};

	const handleUpdateFile = () => {
	  if (!newFilename.trim() || !newFileContent.trim()) return;
	  
	  const newFs = { ...currentLevel.filesystem };
	  
	  // Si cambió el nombre, eliminar el anterior
	  if (editingFile && editingFile !== newFilename) {
		delete newFs[editingFile];
	  }
	  
	  newFs[newFilename] = newFileContent;
	  
	  setCurrentLevel({
		...currentLevel,
		filesystem: newFs
	  });
	  
	  setEditingFile(null);
	  setNewFilename('');
	  setNewFileContent('');
	};

  const handleAddCommandHint = (command) => {
    if (!command.trim()) return;
    setCurrentLevel({
      ...currentLevel,
      commands_hint: [...currentLevel.commands_hint, command]
    });
  };

  const handleRemoveCommandHint = (index) => {
    setCurrentLevel({
      ...currentLevel,
      commands_hint: currentLevel.commands_hint.filter((_, i) => i !== index)
    });
  };

  const handleAddLevel = () => {
    if (!currentLevel.title.trim() || !currentLevel.description.trim() || !currentLevel.password_next_level.trim()) {
      alert('Por favor completa título, descripción y password del nivel');
      return;
    }

    if (editingLevelIndex !== null) {
      // Update existing level
      const updatedLevels = [...quest.levels];
      updatedLevels[editingLevelIndex] = { ...currentLevel };
      setQuest({ ...quest, levels: updatedLevels });
      setEditingLevelIndex(null);
    } else {
      // Add new level
      const levelNumber = quest.levels.length + 1;
      setQuest({
        ...quest,
        levels: [...quest.levels, { ...currentLevel, level_number: levelNumber }]
      });
    }

    // Reset form
    setCurrentLevel({
      level_number: quest.levels.length + 2,
      title: '',
      description: '',
      hint: '',
      filesystem: {},
      password_next_level: '',
      commands_hint: []
    });
  };

  const handleEditLevel = (index) => {
    setCurrentLevel({ ...quest.levels[index] });
    setEditingLevelIndex(index);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setCurrentLevel({
      level_number: quest.levels.length + 1,
      title: '',
      description: '',
      hint: '',
      filesystem: {},
      password_next_level: '',
      commands_hint: []
    });
    setEditingLevelIndex(null);
  };

  const handleRemoveLevel = (index) => {
    const updatedLevels = quest.levels.filter((_, i) => i !== index);
    // Renumber levels
    const renumbered = updatedLevels.map((level, idx) => ({
      ...level,
      level_number: idx + 1
    }));
    setQuest({ ...quest, levels: renumbered });
  };

  const handleSaveQuest = async () => {
    if (!quest.title.trim() || quest.levels.length === 0) {
      alert('Por favor agrega un título y al menos un nivel');
      return;
    }

    try {
      if (isEditMode) {
        await axios.put(`${BACKEND_URL}/api/linux-quest/${questId}`, {
          ...quest,
          teacher_id: quest.teacher_id,
          teacher_name: quest.teacher_name || user.name
        });
        alert('Terminal Quest actualizado exitosamente!');
      } else {
        await axios.post(`${BACKEND_URL}/api/linux-quest`, {
          ...quest,
          teacher_id: user.id,
          teacher_name: user.name
        });
        alert('Terminal Quest creado exitosamente!');
      }
      navigate('/dashboard');
    } catch (error) {
      console.error('Error saving quest:', error);
      alert('Error al guardar el Terminal Quest');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8">
      <div className="max-w-5xl mx-auto px-4">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-3xl font-bold text-gray-800">
              {isEditMode ? '✏️ Editar' : '➕ Crear'} Terminal Quest
            </h1>
            <button
              onClick={() => navigate('/dashboard')}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
            >
              ← Volver
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Título del Quest *</label>
              <input
                type="text"
                value={quest.title}
                onChange={(e) => setQuest({ ...quest, title: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                placeholder="Aventura Linux Básica"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Descripción</label>
              <textarea
                value={quest.description}
                onChange={(e) => setQuest({ ...quest, description: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                placeholder="Aprende comandos básicos de Linux..."
                rows="3"
              />
            </div>

            <div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={quest.is_public}
                  onChange={(e) => setQuest({ ...quest, is_public: e.target.checked })}
                  className="w-4 h-4 text-green-600 rounded"
                />
                <span className="text-sm font-medium text-gray-700">Quest Público</span>
              </label>
              <p className="text-xs text-gray-500 ml-6">Si está desmarcado, solo estudiantes de clases asignadas podrán verlo</p>
            </div>

            {/* Google Classroom Course Selection */}
            {!quest.is_public && (
              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  📚 Asignar a Clases de Google Classroom
                </label>
                {loadingCourses ? (
                  <p className="text-sm text-gray-500">Cargando clases...</p>
                ) : classroomCourses.length === 0 ? (
                  <p className="text-sm text-gray-500">
                    No hay clases disponibles. Conéctate a Google Classroom primero.
                  </p>
                ) : (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {classroomCourses.map((course) => (
                      <label key={course.id} className="flex items-center gap-2 p-2 hover:bg-blue-100 rounded">
                        <input
                          type="checkbox"
                          checked={quest.assigned_courses.includes(course.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setQuest({
                                ...quest,
                                assigned_courses: [...quest.assigned_courses, course.id]
                              });
                            } else {
                              setQuest({
                                ...quest,
                                assigned_courses: quest.assigned_courses.filter(id => id !== course.id)
                              });
                            }
                          }}
                          className="w-4 h-4 text-blue-600 rounded"
                        />
                        <span className="text-sm text-gray-700">{course.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Category Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                🗂️ Categoría (opcional)
              </label>
              <select
                value={quest.category_id || ''}
                onChange={(e) => setQuest({ ...quest, category_id: e.target.value || null })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              >
                <option value="">Sin categoría</option>
                {categories.filter(cat => !cat.parent_id).map(category => (
                  <optgroup key={category.id} label={`${category.icon} ${category.name}`}>
                    <option value={category.id}>
                      {category.icon} {category.name}
                    </option>
                    {categories.filter(sub => sub.parent_id === category.id).map(subcat => (
                      <option key={subcat.id} value={subcat.id}>
                        &nbsp;&nbsp;{subcat.icon} {subcat.name}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>

            <div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={quest.use_real_terminal}
                  onChange={(e) => setQuest({ ...quest, use_real_terminal: e.target.checked })}
                  className="w-4 h-4 text-green-600 rounded"
                />
                <span className="text-sm font-medium text-gray-700">🐳 Usar Terminal Real (Docker)</span>
              </label>
              <p className="text-xs text-gray-500 ml-6">
                Terminal Linux real con todos los comandos. Los archivos se pre-cargan automáticamente.
              </p>
              {quest.use_real_terminal && (
                <div className="ml-6 mt-2 p-3 bg-blue-50 border-l-4 border-blue-500 text-sm text-blue-800">
                  <strong>💡 Tip:</strong> Con terminal real, los estudiantes pueden usar todos los comandos de Linux (mkdir, rm, ps, etc). 
                  Los archivos del filesystem se cargarán en /root/ al iniciar el nivel.
                </div>
              )}
            </div>

            {/* Docker Configuration - Only show if use_real_terminal is enabled */}
            {quest.use_real_terminal && (
              <div className="ml-6 space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h3 className="font-semibold text-gray-700">⚙️ Configuración del Contenedor Docker</h3>
                
                {/* Allow Internet Access */}
                <div>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={quest.allow_internet}
                      onChange={(e) => setQuest({ ...quest, allow_internet: e.target.checked })}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="text-sm font-medium text-gray-700">🌐 Permitir acceso a Internet</span>
                  </label>
                  <p className="text-xs text-gray-500 ml-6">
                    Habilita comandos como ping, wget, curl, apt-get, git, etc.
                  </p>
                </div>

                {/* Persistent Containers */}
                <div>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={quest.persistent_containers}
                      onChange={(e) => setQuest({ ...quest, persistent_containers: e.target.checked })}
                      className="w-4 h-4 text-purple-600 rounded"
                    />
                    <span className="text-sm font-medium text-gray-700">💾 Contenedores Persistentes</span>
                  </label>
                  <p className="text-xs text-gray-500 ml-6">
                    Los alumnos pueden guardar su progreso. El contenedor no se elimina al salir.
                  </p>
                  {quest.persistent_containers && (
                    <div className="ml-6 mt-2 p-2 bg-purple-50 border-l-4 border-purple-500 text-xs text-purple-800">
                      <strong>📌 Nota:</strong> El alumno verá dos botones: "Guardar y Salir" (guarda el contenedor) y "Salir sin Guardar" (elimina el contenedor).
                    </div>
                  )}
                </div>

                {/* Docker Image Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    🐳 Imagen Docker
                  </label>
                  <select
                    value={quest.docker_image}
                    onChange={(e) => setQuest({ ...quest, docker_image: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="alpine:latest">Alpine Linux (Ultra ligera ~5MB, comandos básicos)</option>
                    <option value="ubuntu:22.04">Ubuntu 22.04 (Ligera ~70MB, más herramientas)</option>
                    <option value="debian:bullseye-slim">Debian Slim (Ligera ~80MB, estable)</option>
                    <option value="node:18-alpine">Node.js 18 + Alpine (Para desarrollo Node.js)</option>
                    <option value="python:3.11-alpine">Python 3.11 + Alpine (Para desarrollo Python)</option>
                    <option value="mysql:8">MySQL 8 (Base de datos MySQL)</option>
                    <option value="wordpress:latest">WordPress (WordPress + PHP + Apache)</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Selecciona la imagen según las herramientas que necesites en el quest.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

            {/* Filesystem */}
        {/* Level Editor */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">
            {editingLevelIndex !== null ? '✏️ Editar Nivel' : '➕ Agregar Nuevo Nivel'}
          </h2>
          
          <div className="space-y-4">
            {/* Level Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Título del Nivel *</label>
              <input
                type="text"
                value={currentLevel.title}
                onChange={(e) => setCurrentLevel({ ...currentLevel, title: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                placeholder="Nivel 1: Exploración Básica"
              />
            </div>

            {/* Level Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Descripción *</label>
              <textarea
                value={currentLevel.description}
                onChange={(e) => setCurrentLevel({ ...currentLevel, description: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                placeholder="Encuentra el archivo secreto usando ls y cat..."
                rows="3"
              />
            </div>

            {/* Hint */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Pista (opcional)</label>
              <input
                type="text"
                value={currentLevel.hint}
                onChange={(e) => setCurrentLevel({ ...currentLevel, hint: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                placeholder="Usa 'ls' para listar archivos..."
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Password para Siguiente Nivel *</label>
              <input
                type="text"
                value={currentLevel.password_next_level}
                onChange={(e) => setCurrentLevel({ ...currentLevel, password_next_level: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 font-mono"
                placeholder="secreto123"
              />
            </div>

            {/* Filesystem */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Sistema de Archivos</label>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="mb-3">
                  <input
                    type="text"
                    value={newFilename}
                    onChange={(e) => setNewFilename(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono mb-2"
                    placeholder="archivo.txt o script.sh"
                  />
                  <textarea
                    value={newFileContent}
                    onChange={(e) => setNewFileContent(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono"
                    placeholder="Contenido del archivo (soporta múltiples líneas para scripts .sh)..."
                    rows="6"
                    style={{ whiteSpace: 'pre', fontFamily: 'monospace' }}
                  />
                  <div className="mt-1 text-xs text-gray-500">
                    💡 Para archivos .sh: Usa Enter para crear nuevas líneas. Los saltos de línea se preservarán correctamente.
                  </div>
                </div>
                
                {editingFile ? (
                  <div className="flex gap-2">
                    <button
                      onClick={handleUpdateFile}
                      className="flex-1 px-3 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition text-sm"
                    >
                      ✓ Actualizar Archivo
                    </button>
                    <button
                      onClick={() => {
                        setEditingFile(null);
                        setNewFilename('');
                        setNewFileContent('');
                      }}
                      className="px-3 py-2 bg-gray-400 text-white rounded-lg hover:bg-gray-500 transition text-sm"
                    >
                      Cancelar
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={handleAddFile}
                    className="w-full px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition text-sm"
                  >
                    + Agregar Archivo
                  </button>
                )}

                {Object.keys(currentLevel.filesystem).length > 0 && (
                  <div className="mt-4 space-y-2">
                    {Object.entries(currentLevel.filesystem).map(([filename, content]) => (
                      <div key={filename} className="flex items-center justify-between bg-white p-3 rounded-lg border border-gray-200">
                        <div className="flex-1">
                          <div className="font-mono text-sm font-bold text-gray-800">{filename}</div>
                          <div className="text-xs text-gray-600 mt-1 break-all line-clamp-2">{content || '(vacío)'}</div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEditFile(filename)}
                            className="px-3 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => handleRemoveFile(filename)}
                            className="px-3 py-1 bg-red-500 text-white rounded text-xs hover:bg-red-600"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Buttons */}
            <div className="flex gap-2">
              <button
                onClick={handleAddLevel}
                className={`flex-1 px-6 py-3 text-white rounded-lg font-semibold transition ${
                  editingLevelIndex !== null
                    ? 'bg-yellow-500 hover:bg-yellow-600'
                    : 'bg-green-500 hover:bg-green-600'
                }`}
              >
                {editingLevelIndex !== null ? '✓ Actualizar Nivel' : '+ Agregar Nivel al Quest'}
              </button>
              {editingLevelIndex !== null && (
                <button
                  onClick={handleCancelEdit}
                  className="px-6 py-3 bg-gray-400 text-white rounded-lg font-semibold hover:bg-gray-500 transition"
                >
                  Cancelar
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Levels List */}
        {quest.levels.length > 0 && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">
              Niveles del Quest ({quest.levels.length})
            </h2>
            <div className="space-y-3">
              {quest.levels.map((level, index) => (
                <div key={index} className={`flex items-center justify-between p-4 border-2 rounded-lg ${
                  editingLevelIndex === index ? 'border-yellow-500 bg-yellow-50' : 'border-gray-200'
                }`}>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="px-2 py-1 bg-green-500 text-white text-xs font-bold rounded">
                        Nivel {level.level_number}
                      </span>
                      <span className="font-bold text-gray-800">{level.title}</span>
                      {editingLevelIndex === index && (
                        <span className="px-2 py-1 bg-yellow-500 text-white text-xs font-bold rounded">
                          ✏️ Editando
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600">{level.description}</p>
                    <div className="mt-2 flex items-center gap-4 text-xs text-gray-500">
                      <span>🔑 Password: {level.password_next_level}</span>
                      <span>📁 {Object.keys(level.filesystem).length} archivos</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEditLevel(index)}
                      className="px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => handleRemoveLevel(index)}
                      className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Save Button */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <button
            onClick={handleSaveQuest}
            className="w-full px-6 py-4 bg-green-600 text-white rounded-lg font-bold text-lg hover:bg-green-700 transition"
          >
            {isEditMode ? '💾 Actualizar Terminal Quest' : '🎮 Crear Terminal Quest'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CreateLinuxQuest;
